import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Activity, AlertTriangle, Clock, CalendarClock, Repeat, Sparkles,
  X, ChevronRight, Loader2, BarChart3, TrendingUp, DollarSign,
  Settings as SettingsIcon, Save, RotateCcw, Download
} from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (v) => new Intl.NumberFormat('en-US', {
  style: 'currency', currency: 'USD', maximumFractionDigits: 0
}).format(v || 0);

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = iso.includes('T') ? new Date(iso) : new Date(iso + 'T00:00:00Z');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
  } catch { return '—'; }
};

const urgencyTone = (urgency) => ({
  red: 'bg-red-50 text-red-700 border-red-200',
  orange: 'bg-orange-50 text-orange-700 border-orange-200',
  yellow: 'bg-amber-50 text-amber-700 border-amber-200',
}[urgency] || 'bg-slate-50 text-slate-600 border-slate-200');

const SummaryCard = ({ icon: Icon, label, value, tone = 'indigo', testId }) => {
  const tones = {
    indigo: 'bg-indigo-50 text-indigo-600',
    emerald: 'bg-emerald-50 text-emerald-600',
    amber: 'bg-amber-50 text-amber-600',
    red: 'bg-red-50 text-red-600',
    violet: 'bg-violet-50 text-violet-600',
    rose: 'bg-rose-50 text-rose-600',
  };
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3 hover:shadow-sm transition-shadow" data-testid={testId}>
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${tones[tone]}`}>
        <Icon size={18} />
      </div>
      <div className="min-w-0">
        <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 truncate">{label}</p>
        <p className="text-lg font-black text-slate-900 truncate" style={{ fontFamily: 'Outfit, sans-serif' }}>{value}</p>
      </div>
    </div>
  );
};

const Section = ({ icon: Icon, title, count, tone = 'indigo', children, testId, empty }) => {
  const toneMap = {
    indigo: 'text-indigo-600 bg-indigo-50',
    amber: 'text-amber-600 bg-amber-50',
    red: 'text-red-600 bg-red-50',
    violet: 'text-violet-600 bg-violet-50',
    rose: 'text-rose-600 bg-rose-50',
  };
  return (
    <section className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden mb-5" data-testid={testId}>
      <header className="px-5 py-3 border-b border-slate-100 flex items-center gap-3 bg-slate-50/60">
        <span className={`w-8 h-8 rounded-lg flex items-center justify-center ${toneMap[tone]}`}>
          <Icon size={16} />
        </span>
        <h3 className="text-sm font-bold text-slate-900 flex-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{title}</h3>
        <span className="text-xs font-semibold text-slate-500 bg-slate-100 rounded-full px-2 py-0.5">{count}</span>
      </header>
      {count === 0 ? (
        <div className="px-5 py-8 text-center text-sm text-slate-400">{empty || 'Nothing to flag here — nice work.'}</div>
      ) : (
        <div className="divide-y divide-slate-100">{children}</div>
      )}
    </section>
  );
};

const Row = ({ onClick, children, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className="w-full px-5 py-3 flex items-center gap-4 hover:bg-slate-50 transition-colors text-left group"
  >
    {children}
    <ChevronRight size={16} className="text-slate-300 group-hover:text-indigo-500 transition-colors flex-shrink-0" />
  </button>
);

export const PipelinePulse = ({ open, onOpenChange }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [settings, setSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [pulseRes, settingsRes] = await Promise.all([
        axios.get(`${API_URL}/api/pipeline-pulse`, { withCredentials: true }),
        axios.get(`${API_URL}/api/pipeline-pulse/settings`, { withCredentials: true }),
      ]);
      setData(pulseRes.data);
      setSettings(settingsRes.data);
    } catch (err) {
      toast.error('Failed to load Pipeline Pulse');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (open) {
      setShowSettings(false);
      fetchData();
    }
  }, [open, fetchData]);

  const openAccount = (id) => {
    if (!id) return;
    onOpenChange(false);
    navigate(`/account/${id}`);
  };

  const downloadReport = () => {
    if (!data) return;

    const fmtMoney = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(v || 0);
    const fmt = (iso) => {
      if (!iso) return '—';
      try {
        const d = iso.includes('T') ? new Date(iso) : new Date(iso + 'T00:00:00Z');
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
      } catch { return '—'; }
    };
    const today = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });

    const section = (title, rows) => rows.length === 0 ? '' : `
      <section>
        <h2>${title} <span class="count">${rows.length}</span></h2>
        <table>
          <thead><tr><th>Account</th><th>Stage</th><th>Detail</th><th class="num">ARR</th></tr></thead>
          <tbody>${rows.map(r => `
            <tr>
              <td><strong>${r.account_name || '—'}</strong></td>
              <td>${r.stage_name || '—'}</td>
              <td>${r.detail || ''}</td>
              <td class="num">${fmtMoney(r.arr)}</td>
            </tr>`).join('')}</tbody>
        </table>
      </section>`;

    const inactiveRows = data.inactive.map(a => ({ account_name: a.account_name, stage_name: a.stage_name, arr: a.arr, detail: `${a.days_inactive}d inactive · last activity ${fmt(a.last_activity_at)}` }));
    const stalledRows = data.stalled_proposals.map(a => ({ account_name: a.account_name, stage_name: a.stage_name, arr: a.arr, detail: `${a.days_in_stage}d in stage` }));
    const stuckRows = data.stuck_in_stage.map(a => ({ account_name: a.account_name, stage_name: a.stage_name, arr: a.arr, detail: `${a.days_in_stage}d (threshold ${a.stage_threshold}d)` }));
    const renewalRows = data.upcoming_renewals.map(a => ({ account_name: a.account_name, stage_name: a.stage_name, arr: a.arr, detail: `${a.current_vendor ? a.current_vendor + ' · ' : ''}Renews ${fmt(a.renewal_date)} (${a.days_until_renewal >= 0 ? a.days_until_renewal + 'd' : Math.abs(a.days_until_renewal) + 'd overdue'})` }));
    const reengageRows = data.reengagement.map(a => ({ account_name: a.account_name, stage_name: 'Closed Lost', arr: a.arr, detail: `${a.lost_to ? 'Lost to ' + a.lost_to + ' · ' : ''}Closed ${fmt(a.closed_lost_date)}${a.estimated_renewal_date ? ' · Renewal ' + fmt(a.estimated_renewal_date) : ''}` }));

    const html = `<!doctype html>
<html><head><meta charset="utf-8"/>
<title>Pipeline Pulse — ${today}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: -apple-system, BlinkMacSystemFont, "Inter", "Segoe UI", sans-serif; color: #0f172a; margin: 32px; }
  header { display: flex; justify-content: space-between; align-items: flex-end; border-bottom: 2px solid #4f46e5; padding-bottom: 12px; margin-bottom: 24px; }
  header h1 { margin: 0; font-size: 28px; color: #4f46e5; letter-spacing: -0.02em; }
  header .subtitle { color: #64748b; font-size: 13px; margin-top: 4px; }
  header .date { color: #64748b; font-size: 12px; text-align: right; }
  .summary { display: grid; grid-template-columns: repeat(6, 1fr); gap: 10px; margin-bottom: 28px; }
  .card { border: 1px solid #e2e8f0; border-radius: 8px; padding: 10px 12px; }
  .card .label { font-size: 9px; text-transform: uppercase; letter-spacing: 0.08em; color: #94a3b8; font-weight: 700; margin-bottom: 3px; }
  .card .value { font-size: 18px; font-weight: 800; color: #0f172a; }
  section { margin-bottom: 28px; page-break-inside: avoid; }
  section h2 { font-size: 14px; color: #334155; margin: 0 0 10px; padding-bottom: 6px; border-bottom: 1px solid #e2e8f0; display: flex; align-items: center; gap: 8px; }
  section h2 .count { background: #eef2ff; color: #4f46e5; font-size: 11px; padding: 2px 8px; border-radius: 99px; font-weight: 700; }
  table { width: 100%; border-collapse: collapse; font-size: 12px; }
  th { text-align: left; padding: 8px 10px; background: #f8fafc; color: #475569; font-size: 10px; text-transform: uppercase; letter-spacing: 0.05em; }
  td { padding: 8px 10px; border-bottom: 1px solid #f1f5f9; }
  td.num, th.num { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
  footer { color: #94a3b8; font-size: 10px; text-align: center; margin-top: 32px; border-top: 1px solid #e2e8f0; padding-top: 12px; }
  @media print {
    body { margin: 18px; }
    section { page-break-inside: avoid; }
    .summary { page-break-after: avoid; }
  }
</style>
</head>
<body>
  <header>
    <div>
      <h1>Pipeline Pulse</h1>
      <div class="subtitle">A weekly check-in on the deals that need your attention</div>
    </div>
    <div class="date">${today}</div>
  </header>

  <div class="summary">
    <div class="card"><div class="label">Open Pipeline</div><div class="value">${fmtMoney(data.summary.total_open_arr)}</div></div>
    <div class="card"><div class="label">Open Deals</div><div class="value">${data.summary.total_open_deals}</div></div>
    <div class="card"><div class="label">Needs Attention</div><div class="value">${data.summary.accounts_needing_attention}</div></div>
    <div class="card"><div class="label">Stalled Proposals</div><div class="value">${data.summary.stalled_proposals}</div></div>
    <div class="card"><div class="label">Upcoming Renewals</div><div class="value">${data.summary.upcoming_renewals}</div></div>
    <div class="card"><div class="label">Re-Engage</div><div class="value">${data.summary.reengagement_opportunities}</div></div>
  </div>

  ${section(`Inactive Accounts · ${data.thresholds.inactive_days}+ days`, inactiveRows)}
  ${section(`Stalled Proposals · ${data.thresholds.proposal_stalled_days}+ days`, stalledRows)}
  ${section('Stuck in Same Stage', stuckRows)}
  ${section('Upcoming Renewals · next 90 days', renewalRows)}
  ${section('Re-Engagement Opportunities', reengageRows)}

  <footer>Generated by Deal Desk · Print as PDF from your browser to save a copy</footer>

  <script>window.onload = () => setTimeout(() => window.print(), 250);</script>
</body></html>`;

    const w = window.open('', '_blank');
    if (!w) {
      toast.error('Pop-up blocked — allow pop-ups for Deal Desk to download');
      return;
    }
    w.document.open();
    w.document.write(html);
    w.document.close();
  };

  const saveSettings = async () => {
    if (!settings) return;
    setSavingSettings(true);
    try {
      await axios.put(`${API_URL}/api/pipeline-pulse/settings`, {
        inactive_days: settings.inactive_days,
        proposal_stalled_days: settings.proposal_stalled_days,
        stage_thresholds: settings.stage_thresholds,
        proposal_stage_ids: settings.proposal_stage_ids,
        excluded_stage_ids: settings.excluded_stage_ids || [],
      }, { withCredentials: true });
      toast.success('Pulse settings saved');
      setShowSettings(false);
      fetchData();
    } catch {
      toast.error('Failed to save settings');
    }
    setSavingSettings(false);
  };

  const resetToDefaults = async () => {
    if (!window.confirm('Reset all Pulse thresholds to smart defaults?')) return;
    setSavingSettings(true);
    try {
      await axios.put(`${API_URL}/api/pipeline-pulse/settings`, {
        inactive_days: 10,
        proposal_stalled_days: 14,
        stage_thresholds: {},
        proposal_stage_ids: [],
        excluded_stage_ids: [],
      }, { withCredentials: true });
      toast.success('Reset to defaults');
      fetchData();
    } catch {
      toast.error('Failed to reset');
    }
    setSavingSettings(false);
  };

  const updateStageThreshold = (stageId, days) => {
    setSettings(s => ({
      ...s,
      stage_thresholds: { ...(s.stage_thresholds || {}), [stageId]: days ? parseInt(days) : 0 }
    }));
  };

  const toggleProposalStage = (stageId) => {
    setSettings(s => {
      const current = new Set(s.proposal_stage_ids || []);
      if (current.has(stageId)) current.delete(stageId); else current.add(stageId);
      return { ...s, proposal_stage_ids: Array.from(current) };
    });
  };

  const toggleExcludedStage = (stageId) => {
    setSettings(s => {
      const current = new Set(s.excluded_stage_ids || []);
      if (current.has(stageId)) current.delete(stageId); else current.add(stageId);
      return { ...s, excluded_stage_ids: Array.from(current) };
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl w-[95vw] max-h-[90vh] overflow-y-auto p-0 bg-slate-50" data-testid="pipeline-pulse-modal">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-6 py-4 flex items-center gap-4">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-500 flex items-center justify-center shadow-sm">
            <Activity size={20} className="text-white" />
          </div>
          <div className="flex-1">
            <DialogTitle className="text-xl font-black text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Pipeline Pulse</DialogTitle>
            <p className="text-xs text-slate-500 mt-0.5">A weekly check-in on the deals that need your attention</p>
          </div>
          <Button
            data-testid="download-report-btn"
            onClick={downloadReport}
            disabled={!data}
            className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg px-4 py-2 text-sm font-semibold shadow-sm disabled:opacity-50"
          >
            <Download size={14} className="mr-2" />
            Download Report
          </Button>
          <button
            onClick={() => setShowSettings(s => !s)}
            data-testid="pulse-settings-toggle"
            className={`flex items-center justify-center w-9 h-9 rounded-lg border transition-all ${showSettings ? 'bg-indigo-50 border-indigo-300 text-indigo-600' : 'bg-white border-slate-200 text-slate-500 hover:border-indigo-300 hover:text-indigo-600'}`}
            title="Pulse settings"
          >
            <SettingsIcon size={16} />
          </button>
          <button onClick={() => onOpenChange(false)} className="text-slate-400 hover:text-slate-600 transition-colors" data-testid="pulse-close">
            <X size={20} />
          </button>
        </div>

        <div className="p-6">
          {loading || !data ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 size={28} className="animate-spin text-indigo-600" />
            </div>
          ) : showSettings && settings ? (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6" data-testid="pulse-settings-panel">
              <div className="flex items-center justify-between mb-5">
                <div>
                  <h3 className="text-base font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Pulse Thresholds</h3>
                  <p className="text-xs text-slate-500 mt-0.5">Tune the signals so they match how your team sells.</p>
                </div>
                <button
                  data-testid="pulse-reset-defaults"
                  onClick={resetToDefaults}
                  disabled={savingSettings}
                  className="text-xs font-semibold text-slate-500 hover:text-indigo-600 flex items-center gap-1.5"
                >
                  <RotateCcw size={12} /> Reset
                </button>
              </div>

              {/* Stages to include */}
              <div className="mb-6">
                <label className="text-xs font-bold uppercase tracking-wider text-slate-500">Stages included in Pulse</label>
                <p className="text-xs text-slate-400 mt-0.5">Turn off stages you don't want monitored (e.g. lukewarm leads, nurture).</p>
                <div className="mt-3 space-y-1">
                  {(settings.stages || []).filter(s => !/closed/i.test(s.name)).map(s => {
                    const excluded = (settings.excluded_stage_ids || []).includes(s.id);
                    return (
                      <label
                        key={s.id}
                        data-testid={`pulse-include-stage-${s.id}`}
                        className={`flex items-center gap-3 px-3 py-2 rounded-lg border cursor-pointer transition-all ${excluded ? 'bg-slate-50 border-slate-200 opacity-60' : 'bg-white border-slate-200 hover:border-indigo-300'}`}
                      >
                        <input
                          type="checkbox"
                          checked={!excluded}
                          onChange={() => toggleExcludedStage(s.id)}
                          className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                        />
                        <span className={`text-sm font-semibold flex-1 ${excluded ? 'line-through text-slate-400' : 'text-slate-700'}`}>{s.name}</span>
                        {excluded && <span className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Excluded</span>}
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Global thresholds */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-500">Flag as inactive after</label>
                  <div className="mt-2 flex items-center gap-2">
                    <input
                      data-testid="pulse-setting-inactive-days"
                      type="number"
                      min="1"
                      value={settings.inactive_days}
                      onChange={(e) => setSettings(s => ({ ...s, inactive_days: parseInt(e.target.value) || 1 }))}
                      className="w-24 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-indigo-500"
                    />
                    <span className="text-sm text-slate-500">days without activity</span>
                  </div>
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-500">Stalled proposal after</label>
                  <div className="mt-2 flex items-center gap-2">
                    <input
                      data-testid="pulse-setting-proposal-days"
                      type="number"
                      min="1"
                      value={settings.proposal_stalled_days}
                      onChange={(e) => setSettings(s => ({ ...s, proposal_stalled_days: parseInt(e.target.value) || 1 }))}
                      className="w-24 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-indigo-500"
                    />
                    <span className="text-sm text-slate-500">days in stage</span>
                  </div>
                </div>
              </div>

              {/* Proposal stages */}
              <div className="mb-6">
                <label className="text-xs font-bold uppercase tracking-wider text-slate-500">Treat these stages as "Proposal"</label>
                <p className="text-xs text-slate-400 mt-0.5">Picked stages feed the Stalled Proposals signal.</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {(settings.stages || []).filter(s => !/closed/i.test(s.name) && !(settings.excluded_stage_ids || []).includes(s.id)).map(s => {
                    const active = (settings.proposal_stage_ids || []).includes(s.id);
                    return (
                      <button
                        key={s.id}
                        data-testid={`pulse-proposal-stage-${s.id}`}
                        onClick={() => toggleProposalStage(s.id)}
                        className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-all ${active ? 'bg-indigo-600 border-indigo-600 text-white' : 'bg-white border-slate-200 text-slate-600 hover:border-indigo-300'}`}
                      >
                        {s.name}
                      </button>
                    );
                  })}
                  {(settings.stages || []).filter(s => !/closed/i.test(s.name) && !(settings.excluded_stage_ids || []).includes(s.id)).length === 0 && (
                    <p className="text-xs text-slate-400 italic">No active stages found.</p>
                  )}
                </div>
              </div>

              {/* Per-stage thresholds */}
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-slate-500">"Stuck in stage" thresholds</label>
                <p className="text-xs text-slate-400 mt-0.5">Leave blank to skip a stage entirely.</p>
                <div className="mt-3 space-y-2">
                  {(settings.stages || []).filter(s => !/closed/i.test(s.name) && !(settings.excluded_stage_ids || []).includes(s.id)).map(s => {
                    const val = (settings.stage_thresholds || {})[s.id] || '';
                    return (
                      <div key={s.id} className="flex items-center gap-3 bg-slate-50 rounded-lg px-3 py-2">
                        <span className="text-sm font-semibold text-slate-700 flex-1 truncate">{s.name}</span>
                        <input
                          data-testid={`pulse-threshold-${s.id}`}
                          type="number"
                          min="0"
                          value={val}
                          onChange={(e) => updateStageThreshold(s.id, e.target.value)}
                          placeholder="—"
                          className="w-20 px-3 py-1.5 border border-slate-200 rounded-lg text-sm text-right focus:outline-none focus:border-indigo-500"
                        />
                        <span className="text-xs text-slate-500 w-8">days</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 mt-6 pt-4 border-t border-slate-100">
                <Button variant="outline" onClick={() => setShowSettings(false)} className="rounded-lg border-slate-200">Cancel</Button>
                <Button
                  data-testid="pulse-save-settings"
                  onClick={saveSettings}
                  disabled={savingSettings}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg"
                >
                  {savingSettings ? <Loader2 size={14} className="animate-spin mr-2" /> : <Save size={14} className="mr-2" />}
                  Save Thresholds
                </Button>
              </div>
            </div>
          ) : (
            <>
              {/* Summary cards */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6" data-testid="pulse-summary">
                <SummaryCard testId="pulse-card-open-arr" icon={DollarSign} tone="indigo" label="Open Pipeline" value={formatCurrency(data.summary.total_open_arr)} />
                <SummaryCard testId="pulse-card-open-deals" icon={BarChart3} tone="violet" label="Open Deals" value={data.summary.total_open_deals} />
                <SummaryCard testId="pulse-card-attention" icon={AlertTriangle} tone="amber" label="Needs Attention" value={data.summary.accounts_needing_attention} />
                <SummaryCard testId="pulse-card-stalled" icon={Clock} tone="red" label="Stalled Proposals" value={data.summary.stalled_proposals} />
                <SummaryCard testId="pulse-card-renewals" icon={CalendarClock} tone="rose" label="Upcoming Renewals" value={data.summary.upcoming_renewals} />
                <SummaryCard testId="pulse-card-reengage" icon={Repeat} tone="emerald" label="Re-Engage" value={data.summary.reengagement_opportunities} />
              </div>

              {/* 1. Inactive */}
              <Section testId="pulse-section-inactive" icon={Activity} tone="amber" title={`Inactive Accounts · ${data.thresholds.inactive_days}+ days`} count={data.inactive.length} empty="All accounts touched recently.">
                {data.inactive.map(a => (
                  <Row key={a.id} testId={`pulse-inactive-${a.id}`} onClick={() => openAccount(a.id)}>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-slate-900 truncate">{a.account_name}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{a.stage_name} · Last activity {fmtDate(a.last_activity_at)}</p>
                    </div>
                    <span className="text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-1 whitespace-nowrap">
                      {a.days_inactive}d inactive
                    </span>
                    <span className="text-sm font-bold text-slate-700 w-24 text-right" style={{ fontFamily: 'Outfit, sans-serif' }}>{formatCurrency(a.arr)}</span>
                  </Row>
                ))}
              </Section>

              {/* 2. Stalled proposals */}
              <Section testId="pulse-section-stalled" icon={Clock} tone="red" title={`Stalled Proposals · ${data.thresholds.proposal_stalled_days}+ days`} count={data.stalled_proposals.length} empty="No proposals sitting too long.">
                {data.stalled_proposals.map(a => (
                  <Row key={a.id} testId={`pulse-stalled-${a.id}`} onClick={() => openAccount(a.id)}>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-slate-900 truncate">{a.account_name}</p>
                      <p className="text-xs text-slate-500 mt-0.5">In {a.stage_name} · Last activity {fmtDate(a.last_activity_at)}</p>
                    </div>
                    <span className="text-xs font-semibold text-red-700 bg-red-50 border border-red-200 rounded-full px-2.5 py-1 whitespace-nowrap">
                      {a.days_in_stage}d in stage
                    </span>
                    <span className="text-sm font-bold text-slate-700 w-24 text-right" style={{ fontFamily: 'Outfit, sans-serif' }}>{formatCurrency(a.arr)}</span>
                  </Row>
                ))}
              </Section>

              {/* 3. Stuck in stage */}
              <Section testId="pulse-section-stuck" icon={TrendingUp} tone="violet" title="Stuck in Same Stage" count={data.stuck_in_stage.length} empty="Pipeline is moving smoothly.">
                {data.stuck_in_stage.map(a => (
                  <Row key={a.id} testId={`pulse-stuck-${a.id}`} onClick={() => openAccount(a.id)}>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-slate-900 truncate">{a.account_name}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{a.stage_name} · threshold {a.stage_threshold}d</p>
                    </div>
                    <span className="text-xs font-semibold text-violet-700 bg-violet-50 border border-violet-200 rounded-full px-2.5 py-1 whitespace-nowrap">
                      {a.days_in_stage}d in stage
                    </span>
                    <span className="text-sm font-bold text-slate-700 w-24 text-right" style={{ fontFamily: 'Outfit, sans-serif' }}>{formatCurrency(a.arr)}</span>
                  </Row>
                ))}
              </Section>

              {/* 4. Upcoming renewals */}
              <Section testId="pulse-section-renewals" icon={CalendarClock} tone="rose" title="Upcoming Renewals · 90 days" count={data.upcoming_renewals.length} empty="No renewals on the horizon.">
                {data.upcoming_renewals.map((a, i) => (
                  <Row key={`${a.id || a.account_name}-${i}`} testId={`pulse-renewal-${i}`} onClick={() => openAccount(a.id)}>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-slate-900 truncate">{a.account_name}</p>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {a.current_vendor ? `Vendor: ${a.current_vendor} · ` : ''}Renews {fmtDate(a.renewal_date)}
                      </p>
                    </div>
                    <span className={`text-xs font-semibold rounded-full border px-2.5 py-1 whitespace-nowrap ${urgencyTone(a.urgency)}`}>
                      {a.days_until_renewal >= 0 ? `${a.days_until_renewal}d` : `${Math.abs(a.days_until_renewal)}d overdue`}
                    </span>
                    <span className="text-sm font-bold text-slate-700 w-24 text-right" style={{ fontFamily: 'Outfit, sans-serif' }}>{formatCurrency(a.arr)}</span>
                  </Row>
                ))}
              </Section>

              {/* 5. Re-engagement */}
              <Section testId="pulse-section-reengage" icon={Sparkles} tone="indigo" title="Re-Engagement · lost ~12 months ago" count={data.reengagement.length} empty="No closed-lost prospects in the re-engage window.">
                {data.reengagement.map(a => (
                  <Row key={a.id} testId={`pulse-reengage-${a.id}`} onClick={() => openAccount(a.id)}>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-slate-900 truncate">{a.account_name}</p>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {a.lost_to ? `Lost to ${a.lost_to} · ` : ''}Closed {fmtDate(a.closed_lost_date)}
                        {a.estimated_renewal_date ? ` · Renews ${fmtDate(a.estimated_renewal_date)}` : ''}
                      </p>
                    </div>
                    <span className="text-sm font-bold text-slate-700 w-24 text-right" style={{ fontFamily: 'Outfit, sans-serif' }}>{formatCurrency(a.arr)}</span>
                  </Row>
                ))}
              </Section>

              <p className="text-[11px] text-slate-400 text-center mt-4">
                Generated {fmtDate(data.generated_at)} · Thresholds editable in Settings (coming soon)
              </p>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PipelinePulse;
